import React from 'react';
import { Search, Mail, Eye, Trash2, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatDateDisplay } from '../../lib/datetime';

const UsersTab = ({
  users,
  totalUsers,
  searchTerm,
  setSearchTerm,
  planFilter,
  setPlanFilter,
  currentPage,
  setCurrentPage,
  pageSize,
  appTimeZone,
  handleViewUser,
  handleSendPasswordReset,
  handleDeleteUser,
  mobile = false,
}) => {
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <div className={`flex ${mobile ? 'flex-col gap-4' : 'items-center justify-between'}`}>
          <CardTitle>User Management</CardTitle>
          <div className={`flex ${mobile ? 'flex-col gap-3' : 'items-center gap-4'}`}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 bg-zinc-800 border-zinc-700 ${mobile ? 'w-full' : 'w-64'}`}
              />
            </div>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className={`${mobile ? 'w-full' : 'w-32'} bg-zinc-800 border-zinc-700`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="ALL">All Plans</SelectItem>
                <SelectItem value="FREE">FREE</SelectItem>
                <SelectItem value="BASIC">BASIC</SelectItem>
                <SelectItem value="PRO">PRO</SelectItem>
                <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {mobile ? (
          <div className="space-y-3">
            {users.map((u) => (
              <div key={u.id} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-white">{u.name || 'N/A'}</p>
                    <p className="truncate text-sm text-zinc-500">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={u.subscription_plan === 'FREE' ? 'secondary' : 'default'}>
                      {u.subscription_plan}
                    </Badge>
                    <Badge variant={u.subscription_status === 'ACTIVE' ? 'default' : 'destructive'}>
                      {u.subscription_status}
                    </Badge>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-sm text-zinc-400">
                  <div className="flex items-center gap-2">
                    {u.email_verified ? (
                      <>
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Verified</span>
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4 text-zinc-500" />
                        <span>Unverified</span>
                      </>
                    )}
                  </div>
                  <span>{formatDateDisplay(u.created_at, appTimeZone)}</span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewUser(u.id)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSendPasswordReset(u.id)}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Reset
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-500/30 text-red-400 hover:text-red-300"
                    onClick={() => handleDeleteUser(u.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead>User</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-zinc-800">
                  <TableCell>
                    <div>
                      <p className="font-medium">{u.name || 'N/A'}</p>
                      <p className="text-sm text-zinc-500">{u.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.subscription_plan === 'FREE' ? 'secondary' : 'default'}>
                      {u.subscription_plan}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.subscription_status === 'ACTIVE' ? 'default' : 'destructive'}>
                      {u.subscription_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {u.email_verified ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-zinc-500" />
                    )}
                  </TableCell>
                  <TableCell className="text-zinc-500 text-sm">
                    {formatDateDisplay(u.created_at, appTimeZone)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewUser(u.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSendPasswordReset(u.id)}
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleDeleteUser(u.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        <div className={`mt-4 flex ${mobile ? 'flex-col gap-3' : 'items-center justify-between'}`}>
          <p className="text-sm text-zinc-500">
            Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalUsers)} of {totalUsers}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={(currentPage + 1) * pageSize >= totalUsers}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UsersTab;
